/**
 * Shared visual primitives for the Tasklist ("Pendências") surface.
 *
 * Operator direction 2026-07-21: **the task title is the most prominent thing
 * in a row; everything else is secondary.** So urgency is carried on the LEFT
 * by a small coloured dot — no word, no box, no space stolen from the title —
 * and the wordy badges live together on the RIGHT edge where they read as one
 * quiet column instead of interrupting the sentence. Badges are medium weight
 * at most: a wall of bold small-caps is what made the row feel busy.
 *
 * Colour still signals only what is urgent or wrong; everything else is
 * neutral, matching the Agents pane's status vocabulary.
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { TaskPriority, TaskStatus } from "@/api/lx-tasklist";

/** 4-status chip, right-aligned. Calm by design — a row's urgency comes from
 *  the priority dot and the overdue date, never from its status. */
export function StatusChip({ status }: { status: TaskStatus }) {
  const { t } = useTranslation();
  const cls: Record<TaskStatus, string> = {
    open: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    waiting: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300",
    done: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
    dropped: "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500",
  };
  return (
    <span
      data-testid={`task-status-${status}`}
      className={cn(
        "inline-flex shrink-0 items-center rounded px-2 py-0.5 text-[11px] font-normal",
        cls[status],
      )}
    >
      {t(`tasklist.status.${status}`)}
    </span>
  );
}

/**
 * The LEFT urgency dot — icon, not text.
 *
 * `urgent` red, `high` amber, and nothing at all for `normal`/`low` so the
 * common case adds no ink. It keeps a fixed-width slot either way, so titles
 * stay aligned down the column rather than stepping in and out.
 */
export function PriorityDot({ priority }: { priority: TaskPriority }) {
  const { t } = useTranslation();
  const tone: Partial<Record<TaskPriority, string>> = {
    urgent: "bg-red-500",
    high: "bg-amber-500",
  };
  const cls = tone[priority];
  return (
    <span className="mt-[7px] flex w-2 shrink-0 justify-center" aria-hidden={!cls}>
      {cls && (
        <span
          data-testid={`task-priority-dot-${priority}`}
          title={t(`tasklist.priority.${priority}`)}
          className={cn("h-1.5 w-1.5 rounded-full", cls)}
        />
      )}
    </span>
  );
}

/**
 * The RIGHT priority badge — the word, for the two priorities where it earns
 * its space. Sits beside the status chip so all the labelling is in one column.
 */
export function PriorityBadge({ priority }: { priority: TaskPriority }) {
  const { t } = useTranslation();
  if (priority !== "urgent" && priority !== "high") return null;
  const cls =
    priority === "urgent"
      ? "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"
      : "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400";
  return (
    <span
      data-testid={`task-priority-${priority}`}
      className={cn(
        "inline-flex shrink-0 items-center rounded px-2 py-0.5 text-[11px] font-normal",
        cls,
      )}
    >
      {t(`tasklist.priority.${priority}`)}
    </span>
  );
}
